import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MangaNavbar from "@/components/feature/MangaNavbar";
import MangaFooter from "@/components/feature/MangaFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/MangaBreadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import { comics } from "@/mocks/comics";

type Filter = "全部" | "国漫" | "日漫";

const filters: Filter[] = ["全部", "国漫", "日漫"];

export default function Latest() {
  useSeo({
    title: "最新更新 - 今日连载漫画最新话抢先看 | 天天漫画网",
    description:
      "天天漫画网最新更新页，汇总今日连载漫画最新话。热门日漫、国漫最新章节持续上线，今日最新话抢先看，追更不迷路。",
    keywords: "漫画最新更新,今日更新,漫画每日更新,连载漫画最新话,追更漫画",
    canonicalPath: "/latest",
  });

  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>("全部");
  const query = (searchParams.get("q") || "").trim().toLowerCase();

  const list = useMemo(() => {
    return [...comics].filter(
      (c) =>
        (filter === "全部" || (filter === "国漫" && c.tags.includes("国漫")) || (filter === "日漫" && c.tags.includes("日漫"))) &&
        (!query || c.title.toLowerCase().includes(query) || c.author.toLowerCase().includes(query)),
    );
  }, [filter, query]);

  const breadcrumbItems = [{ label: "首页", to: "/" }, { label: "最新更新" }];

  const listData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "最新更新 - 连载漫画最新话",
    url: "https://panelpeek.github.io/latest",
    inLanguage: "zh-CN",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://panelpeek.github.io/" },
        { "@type": "ListItem", position: 2, name: "最新更新", item: "https://panelpeek.github.io/latest" },
      ],
    },
  };

  return (
    <>
      <JsonLd id="latest-jsonld" data={listData} />
      <MangaNavbar />
      <Breadcrumb items={breadcrumbItems} />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">最新更新</h1>
            <p className="mt-2 text-sm md:text-base text-foreground-500">
              {query ? `「${searchParams.get("q")}」的搜索结果 · ` : ""}每日连载最新话持续上线，第一时间抢先看。
            </p>
          </div>

          <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100 border border-background-200 self-start md:self-auto">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  filter === f ? "bg-primary-500 text-background-50 font-medium" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {list.length === 0 ? (
          <div className="mt-10 rounded-2xl bg-background-100 border border-background-200 p-10 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-foreground-500">没有找到相关漫画，换个关键词试试吧。</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3 md:space-y-4">
            {list.map((comic) => (
              <article
                key={comic.id}
                className="group flex gap-4 md:gap-6 rounded-2xl bg-background-50 border border-background-200 p-3 md:p-4 hover:border-primary-200 transition-colors"
              >
                <Link
                  to={`/comic/${comic.id}`}
                  className="shrink-0 w-20 sm:w-28 md:w-36 aspect-[3/4] rounded-xl overflow-hidden bg-background-100"
                >
                  <img
                    src={comic.cover}
                    alt={`${comic.title} ${comic.latestChapter} 最新话更新`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </Link>
                <div className="flex-1 flex flex-col py-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[11px] text-primary-600 bg-primary-100 px-2 py-0.5 rounded-md font-medium">
                      <i className="ri-flashlight-fill"></i>今日更新
                    </span>
                    <span className="text-[11px] text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-md">{comic.genre}</span>
                    {comic.status === "已完结" && (
                      <span className="text-[11px] text-accent-700 bg-accent-100 px-2 py-0.5 rounded-md">{comic.status}</span>
                    )}
                  </div>
                  <h2 className="mt-2 text-base md:text-xl font-medium text-foreground-900 truncate">
                    <Link to={`/comic/${comic.id}`} className="hover:text-primary-600 transition-colors">
                      {comic.title}
                    </Link>
                  </h2>
                  <p className="mt-1 text-sm text-foreground-600">
                    更新至 <span className="text-primary-600 font-medium">{comic.latestChapter}</span>
                  </p>
                  <p className="mt-1 hidden sm:block text-sm text-foreground-500 truncate">
                    作者：{comic.author} · 已更新 {comic.chapters} 话 · 评分 {comic.rating}
                  </p>
                  <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-foreground-400">{comic.updateTime}</span>
                    <Link
                      to={`/comic/${comic.id}`}
                      className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap"
                    >
                      看最新话 <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      <MangaFooter />
      <BackToTop />
    </>
  );
}